import { isLoadingValue } from "@glide/computation-model-types";
import { asString } from "@glide/common-core/dist/js/computation-model/data";
import {
    type ActionDescription,
    type PropertyDescription,
    ActionKind,
    makeColumnProperty,
} from "@glide/app-description";
import { type TableColumn, isLinkTypeKind } from "@glide/type-schema";
import {
    type AppDescriptionContext,
    type PropertyDescriptor,
    EnumPropertyHandler,
    PropertySection,
    makeInlineTemplatePropertyDescriptor,
    type ActionAvailability,
    type EnumPropertyCase,
} from "@glide/function-utils";
import { getLastURLPathComponent, nullToUndefined, safeURL, tryShortenLinkToHostname } from "@glide/support";
import {
    type WireActionResult,
    type WireActionResultBuilder,
    type WireActionHydrator,
    type WireActionInflationBackend,
} from "@glide/wire";
import { assertNever } from "@glideapps/ts-necessities";
import { LinkAppearance } from "../builder-utils";
import type { StaticActionContext } from "../static-context";
import { type ActionDescriptor, ActionGroup } from "./action-descriptor";
import { type DescriptionToken, actionAvailabilityApps } from "./action-handler";
import { BaseActionHandler, tokenForProperty } from "./base";
import { ICON_PALE } from "../plugins/icon-colors";
import type { GlideIconProps } from "@glide/plugins-codecs";

interface AppearanceData extends EnumPropertyCase<LinkAppearance> {
    uiFunction: string;
}

const linkAppearances: readonly AppearanceData[] = [
    {
        value: LinkAppearance.PageTitle,
        label: "Page title",
        uiFunction: "pageTitleSmartLink",
    },
    {
        value: LinkAppearance.LastPathComponent,
        label: "Last path component",
        uiFunction: "lastPathComponentSmartLink",
    },
    {
        value: LinkAppearance.ShortURL,
        label: "Short URL",
        uiFunction: "shortURLSmartLink",
    },
    {
        value: LinkAppearance.FullURL,
        label: "Full URL",
        uiFunction: "urlSmartLink",
    },
];

export function getTitleAndCaptionForAppearance(
    appearance: LinkAppearance,
    url: string,
    title: string | undefined,
    caption: string | undefined
): [title: string | undefined, caption: string | undefined] {
    switch (appearance) {
        case LinkAppearance.PageTitle:
            return [title, caption];

        case LinkAppearance.LastPathComponent:
            return [getLastURLPathComponent(url) ?? tryShortenLinkToHostname(url), caption];

        case LinkAppearance.ShortURL:
            return [tryShortenLinkToHostname(url), caption];

        case LinkAppearance.FullURL:
            return [url, caption];

        case LinkAppearance.CaptionAsTitle:
            return [caption ?? undefined, undefined];

        default:
            return assertNever(appearance);
    }
}

export interface OpenWebViewActionDescription extends ActionDescription {
    // This property name is also hardcoded in ##convertLegacyActions
    readonly link: PropertyDescription | undefined;
    readonly appearance: PropertyDescription | undefined;
}

export const webViewAppearancePropertyHandler = new EnumPropertyHandler(
    { appearance: LinkAppearance.PageTitle },
    "Title",
    "Show as",
    linkAppearances,
    PropertySection.Design,
    "dropdown"
);

interface OpenLinkActionDescription extends OpenWebViewActionDescription {}

export function makeLinkColumnPropertyDescriptor(): PropertyDescriptor {
    return makeInlineTemplatePropertyDescriptor("link", "URL", "Enter URL", true, "withLabel", {
        preferredNames: ["link", "url"],
        preferredType: "uri",
        searchable: false,
        applyFormat: false,
        columnFirst: true,
    });
}

export class OpenLinkWithArgsHandler extends BaseActionHandler<OpenLinkActionDescription> {
    public readonly kind = ActionKind.OpenLinkWithArgs;

    public readonly iconName: GlideIconProps = {
        icon: "st-open-link",
        kind: "stroke",
        strokeFgColor: ICON_PALE,
    };

    public readonly name: string = "Open link";

    public get availability(): ActionAvailability {
        return actionAvailabilityApps;
    }

    public getDescriptor(): ActionDescriptor {
        const properties: PropertyDescriptor[] = [makeLinkColumnPropertyDescriptor()];

        return {
            name: this.name,
            group: ActionGroup.Interaction,
            groupItemOrder: 10,
            needsScreenContext: true,
            properties,
        };
    }

    public newDefaultActionDescription(c: TableColumn): OpenLinkActionDescription | undefined {
        if (!isLinkTypeKind(c.type.kind)) return undefined;
        return {
            kind: ActionKind.OpenLinkWithArgs,
            link: makeColumnProperty(c.name),
            appearance: undefined,
        };
    }

    public getTokenizedDescription(
        desc: OpenLinkActionDescription,
        env: StaticActionContext<AppDescriptionContext>
    ): readonly DescriptionToken[] | undefined {
        const token = tokenForProperty(desc.link, env);
        if (token === undefined) return undefined;
        return [token];
    }

    public inflate(
        ib: WireActionInflationBackend,
        desc: OpenLinkActionDescription,
        arbBase: WireActionResultBuilder
    ): WireActionHydrator | WireActionResult {
        const [linkGetter, linkType] = ib.getValueGetterForProperty(desc.link, false);
        if (linkType === undefined) return arbBase.inflationError("Invalid link");

        const appearance = webViewAppearancePropertyHandler.getEnum(desc);

        return (vp, skipLoading) => {
            const linkValue = nullToUndefined(linkGetter(vp));
            if (isLoadingValue(linkValue)) return arbBase.maybeSkipLoading(skipLoading, "URL");
            const linkValueString = asString(linkValue).trim();
            const link = safeURL(linkValueString);

            const arb = arbBase.addData({ link: link ?? linkValueString, appearance });
            if (link === undefined) return arb.error(true, "Invalid URL");

            return [
                async (ab, handledByFrontend) => {
                    if (!handledByFrontend) {
                        ab.actionCallbacks.openLink(link);
                    }
                    return arb.success();
                },
                link,
            ];
        };
    }
}
